import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './ConfirmDialog.css';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  busy = false,
  onConfirm,
  onCancel
}: ConfirmDialogProps) => {
  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [busy, onCancel, open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="confirm-dialog-overlay"
      role="presentation"
      onClick={() => {
        if (!busy) {
          onCancel();
        }
      }}
    >
        <div
          className="confirm-dialog-card"
          role="dialog"
          aria-modal="true"
          aria-label={title}
        onClick={(event) => event.stopPropagation()}
        >
        <div className="confirm-dialog-header">
          <h3>{title}</h3>
          <button
            type="button"
            className="confirm-dialog-close touch-target"
            aria-label="Close confirmation dialog"
            onClick={() => {
              if (!busy) {
                onCancel();
              }
            }}
            disabled={busy}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="confirm-dialog-body">
          {description}
        </div>
        <div className="confirm-dialog-actions">
          <button type="button" className="btn ghost confirm-dialog-action touch-target" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn ${confirmVariant === 'danger' ? 'danger' : 'primary'} confirm-dialog-action touch-target`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
